import type { SubmitHandler } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import type { AddressSchemaType } from 'src/components/checkout/steps/shipping/components/Address/AddressForm'
import { createAddressSchema } from 'src/components/checkout/steps/schemas/zodSchemas'
import type { AddressData } from 'src/sdk/checkout/data/types/orderformAPI'
import type { OrderForm as CheckoutOrderForm } from 'src/sdk/checkout/entity/orderForm'

import { resolveAddressReferenceLabel } from '../../utils/addressReferenceLabel'
import type { LocalAddressesPayload } from '../../domain/addressManagerReconciliation'
import {
  OPTIONAL_ADDRESS_FIELDS,
  buildCheckoutAddressFromFormValues,
  buildPostalInputDisabledFlags,
  persistCheckoutAddress,
} from '../../domain/addressFormPersistence'
import { PostalFormFields } from '../Input/PostalFormFields'

interface AddressFormModalProps {
  data: Partial<AddressData> | null
  isReadOnly?: boolean
  onSubmit: (data: AddressSchemaType, savedAddress?: AddressData) => void
  onCancel: () => void
  isLoading?: boolean
  orderForm: {
    orderFormId: string
  }
  setOrderForm: (orderForm: CheckoutOrderForm) => void
  onUpdateLocalAddresses?: (payload: LocalAddressesPayload) => void
  editableFields?: string[]
  submitButtonLabel?: string
}

export function AddressFormModal({
  data,
  onSubmit,
  isLoading,
  orderForm,
  setOrderForm,
  onCancel,
  onUpdateLocalAddresses,
  editableFields = ['number', 'complement', 'apelido', 'receiverName'],
  submitButtonLabel,
}: AddressFormModalProps) {
  const schema = useMemo(
    () =>
      createAddressSchema(OPTIONAL_ADDRESS_FIELDS.map((field) => field.name)),
    []
  )

  const { orderFormId } = orderForm

  const referenceLabelFromData = resolveAddressReferenceLabel(data)

  const defaultFormValues = {
    street: data?.street ?? '',
    city: data?.city ?? '',
    state: data?.state ?? '',
    neighborhood: data?.neighborhood ?? '',
    number: data?.number ?? '',
    complement: data?.complement ?? '',
    receiverName: data?.receiverName ?? '',
    reference: referenceLabelFromData,
  }

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors: formErrors },
    reset,
  } = useForm<AddressSchemaType>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: defaultFormValues,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const isFormLoading = Boolean(isLoading) || isSubmitting

  const syncFormReference = useCallback(
    (value: string) => {
      setValue('reference', value, {
        shouldValidate: false,
        shouldTouch: false,
      })
    },
    [setValue]
  )

  const disabledFields = buildPostalInputDisabledFlags(data, editableFields)

  const submitAddressFlow: SubmitHandler<AddressSchemaType> = async (info) => {
    if (isFormLoading) return

    setIsSubmitting(true)

    try {
      await schema.parseAsync(info)

      const checkoutAddressPayload = buildCheckoutAddressFromFormValues(
        info,
        data
      )

      const updatedOrderForm = await persistCheckoutAddress({
        orderFormId,
        address: checkoutAddressPayload,
        setOrderForm,
      })

      const updatedAddresses =
        updatedOrderForm?.shippingData?.availableAddresses ?? []

      const selectedAddressId =
        updatedOrderForm?.shippingData?.address?.addressId ??
        updatedOrderForm?.shippingData?.selectedAddresses?.[0]?.addressId ??
        null

      const savedAddress =
        updatedAddresses.find((item) => item.addressId === selectedAddressId) ??
        updatedAddresses.find(
          (item) =>
            item.addressId === checkoutAddressPayload.addressId &&
            checkoutAddressPayload.addressId !== ''
        ) ??
        updatedAddresses[updatedAddresses.length - 1]

      if (updatedAddresses.length > 0) {
        onUpdateLocalAddresses?.({ updatedAddresses, selectedAddressId })
      }

      onSubmit(info, savedAddress)
      reset()
    } catch (err) {
      console.error('Error updating address:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      className="w-full flex flex-col gap-[21px]"
      onSubmit={handleSubmit(submitAddressFlow)}
    >
      <PostalFormFields
        formErrors={formErrors}
        register={register}
        isLoading={isFormLoading}
        disabledFields={disabledFields}
        onCancel={onCancel}
        submitButtonLabel={submitButtonLabel}
        initialReference={referenceLabelFromData}
        syncFormReference={syncFormReference}
      />
    </form>
  )
}
